/**
 * DraftTab — the Inspector's primary surface: one section's drafted text
 * rendered as Markdown, and nothing else (no per-sentence citation reveals or
 * citation strips — the user wants the section output only).
 *
 * Data source: getSectionContent(runId, moduleId) — returns the draft sections.
 *
 * Component stability: the markdown component overrides are memoized once per
 * DraftTab instance so react-markdown never remounts the subtree.
 */

import { Spinner, makeStyles } from "@fluentui/react-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { getSectionContent } from "../../../api/client";
import { remarkSubSup } from "../../../services/remarkSubSup";
import { remarkSoftBreaks } from "../../../services/remarkSoftBreaks";
import { decodeEscapedWhitespace, normalizeTableSpacing } from "../../../services/markdownNormalize";
import { repairPartialTables } from "../../../services/partialTables";
import type { SpineSection } from "../../hooks/useVariantCRun";
import { sanitizeDraftForWord } from "../../services/draftSanitizer";
import { hasTitleFields } from "../../services/titleFieldsView";
import { fonts, inkColors, surfaceColors } from "../../tokens";
import TitleFieldsPreview from "./TitleFieldsPreview";

interface DraftTabProps {
  section: SpineSection;
  runId: string;
}

interface SectionPayload {
  sections: Array<{
    section_number: string;
    section_title: string;
    draft_content: string;
  }>;
  /** Structured title-page fields (label→value). A title-page module has no
   *  prose body — its content lives here. Attached at the top level of the
   *  section response by the backend; rendered via TitleFieldsPreview. */
  title_fields?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Block-structure helpers — lists, soft breaks, bold-only "headers"
// ---------------------------------------------------------------------------

/**
 * React context flag: true while rendering inside a <ul>/<ol>. The paragraph
 * renderer reads it to skip the bold-line→<h4> promotion that only makes sense
 * for top-level prose — inside a list item that machinery shatters the list.
 */
const InListContext = React.createContext(false);

/**
 * A paragraph whose only meaningful child is a single **bold** run is the
 * model's idiom for a sub-heading (e.g. a standalone `**Overview**` line). We
 * promote ONLY that exact shape to <h4>. We inspect the hast node (not the
 * already-rendered React children, whose <strong> is our own component) so the
 * check stays reliable. The old length/punctuation heuristic mis-promoted
 * ordinary short sentences and list items, so real headings looked wrong.
 */
function isBoldOnlyParagraph(node: unknown): boolean {
  const children = (node as { children?: Array<{ type?: string; tagName?: string; value?: string }> })?.children;
  if (!Array.isArray(children)) return false;
  const meaningful = children.filter((c) => !(c.type === "text" && /^\s*$/.test(c.value ?? "")));
  return meaningful.length === 1 && meaningful[0].type === "element" && meaningful[0].tagName === "strong";
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    padding: "14px 16px 24px",
    fontFamily: fonts.body,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "20px 0",
    color: inkColors.ink3,
    fontSize: "12px",
  },
  // Same calm centered empty state as the other inspector tabs.
  emptyState: {
    padding: "22px 8px",
    color: inkColors.ink3,
    fontSize: "12px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  errorBanner: {
    padding: "8px 10px",
    background: "#FEF2F2",
    border: "1px solid #FCA5A5",
    borderRadius: "3px",
    fontSize: "11px",
    color: "#7F1D1D",
    marginBottom: "10px",
  },
  paragraph: {
    marginBottom: "12px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
  },
  sectionMeta: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "8px",
  },
  h2: {
    fontSize: "14px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "16px",
    marginBottom: "10px",
  },
  h3: {
    fontSize: "13px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "14px",
    marginBottom: "8px",
  },
  h4: {
    fontSize: "12px",
    fontWeight: 700,
    color: inkColors.ink,
    marginTop: "12px",
    marginBottom: "8px",
  },
  ul: {
    display: "block",
    marginTop: 0,
    marginBottom: "12px",
    paddingLeft: "22px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
    listStyleType: "disc",
    listStylePosition: "outside",
  },
  ol: {
    display: "block",
    marginTop: 0,
    marginBottom: "12px",
    paddingLeft: "22px",
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
    listStyleType: "decimal",
    listStylePosition: "outside",
  },
  li: {
    display: "list-item",
    marginBottom: "4px",
  },
  // Paragraph rendered inside a list item: plain prose and no block decoration, so the list marker and spacing stay clean.
  listParagraph: {
    margin: 0,
    fontSize: "12px",
    color: inkColors.ink,
    lineHeight: 1.7,
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    marginBottom: "12px",
    fontSize: "12px",
    border: `1px solid ${surfaceColors.line}`,
    // repairPartialTables emits a blank header row for a table cut above its header.
    "& thead:not(:has(th:not(:empty)))": { display: "none" },
  },
  th: {
    backgroundColor: surfaceColors.paperDim,
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 700,
    color: inkColors.ink,
    // Full cell border so the grid shows both horizontal AND vertical rules.
    border: `1px solid ${surfaceColors.line}`,
    verticalAlign: "top",
  },
  td: {
    padding: "6px 8px",
    border: `1px solid ${surfaceColors.line}`,
    color: inkColors.ink,
    verticalAlign: "top",
  },
  strong: {
    fontWeight: 700,
    color: inkColors.ink,
  },
  em: {
    fontStyle: "italic",
    color: inkColors.ink,
  },
  // ~sub~ / ^sup^ markers (remarkSubSup). line-height 0 stops the raised /
  // lowered glyph from stretching the line box (jitter between lines).
  subSup: {
    fontSize: "0.75em",
    lineHeight: 0,
    color: inkColors.ink,
  },
  code: {
    backgroundColor: surfaceColors.paperDim,
    padding: "2px 4px",
    borderRadius: "2px",
    fontFamily: fonts.mono,
    fontSize: "11px",
    color: inkColors.ink2,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DraftTab: React.FC<DraftTabProps> = ({ section, runId }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<SectionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const requestKeyRef = useRef<string>("");

  // A failed section produced no body — don't fetch (the content route 404s).
  const draftFailed = !!section.failed && section.status === "flagged";
  const hasDraftContent =
    !draftFailed &&
    (section.status === "drafted" || section.status === "needs_approval" || section.status === "flagged");

  useEffect(() => {
    if (!hasDraftContent) {
      setLoading(false);
      setError(null);
      setPayload(null);
      return;
    }
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setPayload(null);
    (async () => {
      try {
        const resp = await getSectionContent(runId, section.id);
        if (cancelled || requestKeyRef.current !== key) return;
        setPayload(resp as unknown as SectionPayload);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id, hasDraftContent]);

  // Memoize the components object so react-markdown never remounts
  const markdownComponents: Components = useMemo(() => {
    return {
      p: ({ node, children, ...props }) => {
        const insideList = React.useContext(InListContext);

        // Inside a list item: plain prose, no bold→<h4> promotion (it would
        // shatter the list marker and spacing).
        if (insideList) {
          return <div className={styles.listParagraph}>{children}</div>;
        }

        // A standalone **bold** line is the model's sub-heading idiom → <h4>.
        if (isBoldOnlyParagraph(node)) {
          return (
            <h4 className={styles.h4} {...props}>
              {children}
            </h4>
          );
        }

        return <div className={styles.paragraph}>{children}</div>;
      },

      h2: ({ children, ...props }) => (
        <h2 className={styles.h2} {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3 className={styles.h3} {...props}>
          {children}
        </h3>
      ),
      h4: ({ children, ...props }) => (
        <h4 className={styles.h4} {...props}>
          {children}
        </h4>
      ),
      ul: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ul className={styles.ul} {...props}>
            {children}
          </ul>
        </InListContext.Provider>
      ),
      ol: ({ node: _node, children, ...props }) => (
        <InListContext.Provider value={true}>
          <ol className={styles.ol} {...props}>
            {children}
          </ol>
        </InListContext.Provider>
      ),
      li: ({ node: _node, children, ...props }) => (
        <li className={styles.li} {...props}>
          {children}
        </li>
      ),
      table: ({ children, ...props }) => (
        <table className={styles.table} {...props}>
          {children}
        </table>
      ),
      th: ({ children, ...props }) => (
        <th className={styles.th} {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td className={styles.td} {...props}>
          {children}
        </td>
      ),
      strong: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      b: ({ children, ...props }: any) => (
        <strong className={styles.strong} {...props}>
          {children}
        </strong>
      ),
      em: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      i: ({ children, ...props }: any) => (
        <em className={styles.em} {...props}>
          {children}
        </em>
      ),
      code: ({ children, ...props }: any) => (
        <code className={styles.code} {...props}>
          {children}
        </code>
      ),
      sub: ({ children, ...props }: any) => (
        <sub className={styles.subSup} {...props}>
          {children}
        </sub>
      ),
      sup: ({ children, ...props }: any) => (
        <sup className={styles.subSup} {...props}>
          {children}
        </sup>
      ),
    };
  }, [styles]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <Spinner size="extra-tiny" />
          <span>Loading draft…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.errorBanner}>Couldn&apos;t load this section&apos;s draft: {error}</div>
      </div>
    );
  }

  if (draftFailed) {
    return (
      <div className={styles.root}>
        <div className={styles.errorBanner}>
          Drafting failed for this section{section.failureReason ? ` — ${section.failureReason}` : ""}. Use REGEN to
          retry.
        </div>
      </div>
    );
  }

  if (!hasDraftContent) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>This section hasn&apos;t been drafted yet. Click Generate to draft it.</div>
      </div>
    );
  }

  const sectionsBlocks = payload?.sections ?? [];

  // Title-page modules carry no prose body — their content is a structured
  // ``title_fields`` dict (empty ``draft_content``). Render those fields the way
  // wordWriter.ts::updateTitlePageFields paints them into the live doc, instead
  // of the (empty) markdown map that leaves the panel blank. Prefer this whenever
  // the section has title fields and no real prose body.
  const titleFields = payload?.title_fields;
  const hasProseBody = sectionsBlocks.some((b) => b.draft_content?.trim());
  if (titleFields && hasTitleFields(titleFields) && !hasProseBody) {
    return (
      <div className={styles.root}>
        <div className={styles.sectionMeta}>
          {section.number} · {section.title}
        </div>
        <TitleFieldsPreview fields={titleFields} />
      </div>
    );
  }

  if (sectionsBlocks.length === 0) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>No content available for this section.</div>
      </div>
    );
  }

  // No number/title eyebrow here: the Inspector header already names the section.
  return (
    <div className={styles.root}>
      {sectionsBlocks.map((block, blockIdx) => {
        const sanitized = normalizeTableSpacing(
          repairPartialTables(sanitizeDraftForWord(decodeEscapedWhitespace(block.draft_content))),
        );

        return (
          <div key={`${block.section_number}-${blockIdx}`}>
            {/* singleTilde:false — GFM's default single-tilde strikethrough
                would consume `~2~` before remarkSubSup can see it. */}
            <ReactMarkdown
              remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSoftBreaks, remarkSubSup]}
              components={markdownComponents}
            >
              {sanitized}
            </ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
};

export default DraftTab;
